import { Subscribable } from '@benzinga/subscribable';
import { WidgetLink, WidgetLinkingID } from './entities';
import { LinkFeedEvents, LinkFeed as WidgetLinkFeed } from './feed';
import * as uuid from 'uuid';
import { Session } from '@benzinga/session';

interface LinksUpdatedEvent {
  links: WidgetLink[];
  type: 'linking:links_updated';
}

export type WidgetLinkingManagerEvent = LinksUpdatedEvent | LinkFeedEvents;

export class WidgetLinkingManager extends Subscribable<WidgetLinkingManagerEvent> {
  private static defaultLinkFeed = new WidgetLinkFeed({
    events: [],
    hue: null,
    id: null,
    title: '',
  });
  private groupLinksByID: Map<WidgetLinkingID, WidgetLinkFeed>;
  private subscriptionsByID: Map<WidgetLinkingID, ReturnType<WidgetLinkFeed['subscribe']>>;
  private channel: BroadcastChannel;

  constructor(session: Session) {
    super();
    this.groupLinksByID = new Map();
    this.subscriptionsByID = new Map();

    this.channel = new BroadcastChannel('linkingManager');
    this.channel.onmessage = (e: MessageEvent<WidgetLinkingManagerEvent>) => {
      if (e.data.type !== 'linking:links_updated') return;
      e.data.links.forEach(link => this.saveWidgetLink(link));
    };
    this.subscribe(event => {
      if (event.type !== 'linking:links_updated') return;
      this.channel.postMessage(event);
    });
    session.onPreSessionClose(() => {
      this.channel.close();
    });
  }
  public static getName = () => 'benzinga-widget-linking';
  public getWidgetLinks() {
    return Array.from(this.groupLinksByID.values()).map(feed => feed.getLink());
  }
  public getDefaultLinkFeed() {
    return WidgetLinkingManager.defaultLinkFeed;
  }
  public getWidgetLinkFeedByID(groupID: WidgetLinkingID) {
    if (groupID === null) return this.getDefaultLinkFeed();
    return this.groupLinksByID.get(groupID);
  }
  public saveWidgetLink(widgetGroup: Partial<WidgetLink>): WidgetLinkFeed {
    if (widgetGroup.id === null) delete widgetGroup['id'];

    const feeds = Array.from(this.groupLinksByID.values());
    const foundFeed = feeds.find(feed => feed.getLink().id === widgetGroup.id);

    if (foundFeed) {
      foundFeed.editLink(widgetGroup);
      return foundFeed;
    }

    const newGroup: WidgetLink = {
      events: [],
      hue: 0,
      id: uuid.v4(),
      title: 'New Group',
      ...widgetGroup,
    };

    const feed = new WidgetLinkFeed(newGroup);
    this.groupLinksByID.set(newGroup.id, feed as unknown as WidgetLinkFeed);

    this.dispatch({
      links: Array.from(this.groupLinksByID.values()).map(feed => feed.getLink()),
      type: 'linking:links_updated',
    });

    // fire off group updated event whenever we get feed event
    this.subscriptionsByID.get(newGroup.id)?.unsubscribe();
    this.subscriptionsByID.set(newGroup.id, feed.subscribe(this.subscriptionHandler));

    return feed;
  }
  private subscriptionHandler = (e: LinkFeedEvents) => {
    this.dispatch(e);
    switch (e.type) {
      case 'linking:link_updated':
        this.dispatch({
          links: Array.from(this.groupLinksByID.values()).map(feed => feed.getLink()),
          type: 'linking:links_updated',
        });
        break;
    }
  };
}
